import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Send, PlayCircle, PauseCircle, XCircle, Eye, Pencil, Copy } from 'lucide-react';
import { CONTACT_STATUS_LABELS } from './ContactBadges';
import { CampaignDetailsAndAudience, CampaignCadenceSettings } from './CampaignFormFields';

const DASHBOARD_TILES = ['remaining', 'sent', 'replied', 'interested', 'follow_up', 'not_interested', 'bounced', 'do_not_contact'];
const EDITABLE_STATUSES = ['draft', 'paused'];

export default function EmailCampaignDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [campaign, setCampaign] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [activeFilter, setActiveFilter] = useState(null);
    const [preview, setPreview] = useState(null);
    const [testEmail, setTestEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [pinnedCompanies, setPinnedCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [companyFilters, setCompanyFilters] = useState({ status: '__any__', contact_email: '', date_from: '', date_to: '', source: '__any__' });
    const [autoEditDone, setAutoEditDone] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/leadgen/email-campaigns/${id}`);
            setCampaign(r.data);
            if (r.data.status !== 'draft') {
                const d = await api.get(`/leadgen/email-campaigns/${id}/dashboard`);
                setDashboard(d.data);
            }
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    }, [id]);
    // React Router reuses this component instance across /leadgen/email-campaigns/:id
    // navigations (e.g. Duplicate → navigate to the new id) — reset stale state from
    // the PREVIOUS campaign before the fresh one loads, so autoEdit/editForm never
    // briefly renders the old campaign's audience/filters.
    useEffect(() => {
        setCampaign(null);
        setDashboard(null);
        setEditing(false);
        setPreview(null);
        setAutoEditDone(false);
    }, [id]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!campaign || campaign.status === 'draft') return;
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, [campaign, load]);

    const loadCompanies = useCallback(async () => {
        if (!editing) return;
        try {
            const r = await api.get('/clients', { params: {
                search, per_page: 200,
                status: companyFilters.status === '__any__' ? undefined : companyFilters.status,
                contact_email: companyFilters.contact_email || undefined,
                date_from: companyFilters.date_from || undefined,
                date_to: companyFilters.date_to || undefined,
                source: companyFilters.source === '__any__' ? undefined : companyFilters.source,
            } });
            setCompanies(r.data.items);
        } catch { /* noop */ }
    }, [editing, search, companyFilters]);
    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    const startEdit = useCallback(() => {
        const preselected = campaign.filters?.client_ids || [];
        setEditForm({
            name: campaign.name, subject: campaign.subject, body_html: campaign.body_html,
            client_ids: preselected,
            only_new_leads: campaign.filters?.only_new_leads || false,
            keyword: campaign.filters?.keyword || '',
            verified_only: campaign.filters?.verified_only || false,
            exclude_contacted: campaign.filters?.exclude_contacted !== false,
            bounce_correction_import_id: campaign.filters?.bounce_correction_import_id || null,
            cadence_seconds: campaign.cadence_seconds, daily_cap: campaign.daily_cap,
        });
        setEditing(true);
        // Pre-selected companies may fall outside the default filter/search view — fetch
        // them directly so the checklist still shows (and allows deselecting) them.
        if (preselected.length) {
            Promise.all(preselected.map(cid => api.get(`/clients/${cid}`).then(r => r.data).catch(() => null)))
                .then(results => setPinnedCompanies(results.filter(Boolean)));
        } else {
            setPinnedCompanies([]);
        }
    }, [campaign]);

    // Landing here right after "Duplicate" — jump straight into the audience picker.
    // Guard on campaign.id === id (not just truthy campaign) — React Router reuses this
    // component instance across id changes, so `campaign` can still hold the PREVIOUS
    // campaign's data (e.g. its client_ids) for one render before load() overwrites it.
    useEffect(() => {
        if (campaign && campaign.id === id && campaign.status === 'draft' && location.state?.autoEdit && !autoEditDone) {
            setAutoEditDone(true);
            startEdit();
        }
    }, [campaign, id, location.state, autoEditDone, startEdit]);

    const saveEdit = async () => {
        if (!editForm.name.trim()) return toast.error('Give the campaign a name');
        if (!editForm.subject.trim() || !editForm.body_html.trim()) return toast.error('Add a subject and body');
        setSaving(true);
        try {
            const payload = campaign.status === 'draft'
                ? { ...editForm, client_ids: editForm.client_ids.length ? editForm.client_ids : null }
                : { name: editForm.name, subject: editForm.subject, body_html: editForm.body_html, cadence_seconds: editForm.cadence_seconds, daily_cap: editForm.daily_cap };
            await api.patch(`/leadgen/email-campaigns/${id}`, payload);
            toast.success('Campaign updated');
            setEditing(false);
            setPreview(null);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setSaving(false); }
    };

    const duplicateCampaign = async () => {
        setDuplicating(true);
        try {
            const r = await api.post(`/leadgen/email-campaigns/${id}/duplicate`);
            toast.success('Campaign duplicated — pick a new audience below');
            navigate(`/leadgen/email-campaigns/${r.data.id}`, { state: { autoEdit: true } });
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setDuplicating(false); }
    };

    const loadRecipients = useCallback(async (status) => {
        setActiveFilter(status);
        try {
            const r = await api.get(`/leadgen/email-campaigns/${id}/recipients`, { params: status ? { status } : {} });
            setRecipients(r.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    }, [id]);

    const runPreview = async () => {
        try {
            const r = await api.post('/leadgen/email-campaigns/preview', {
                client_ids: campaign.filters?.client_ids || null,
                only_new_leads: campaign.filters?.only_new_leads || false,
                keyword: campaign.filters?.keyword || null,
                verified_only: campaign.filters?.verified_only || false,
                exclude_contacted: campaign.filters?.exclude_contacted !== false,
                bounce_correction_import_id: campaign.filters?.bounce_correction_import_id || null,
                subject: campaign.subject, body_html: campaign.body_html,
            });
            setPreview(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    };

    const doAction = async (action, confirmMsg) => {
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        setBusy(true);
        try {
            const r = await api.post(`/leadgen/email-campaigns/${id}/${action}`);
            toast.success(action === 'queue' ? `Queued ${r.data.queued} recipients` : 'Done');
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const testSend = async () => {
        if (!testEmail) return toast.error('Enter a recipient email');
        setBusy(true);
        try {
            await api.post(`/leadgen/email-campaigns/${id}/test-send`, { to: testEmail });
            toast.success(`Test sent to ${testEmail}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    if (!campaign) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <div data-testid="email-campaign-detail-page" className="bm-fade-in">
            <PageHeader
                title={campaign.name}
                description={`${campaign.subject} · ${campaign.cadence_seconds}s between emails · max ${campaign.daily_cap}/day`}
                actions={<div className="flex items-center gap-2">
                    <Button variant="outline" onClick={duplicateCampaign} disabled={duplicating} className="h-9" data-testid="campaign-duplicate-btn">
                        <Copy size={15} className="mr-1.5" /> {duplicating ? 'Duplicating…' : 'Duplicate'}
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/leadgen/email-campaigns')} className="h-9"><ArrowLeft size={15} className="mr-1" /> Back</Button>
                </div>}
            >
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10.5px] font-semibold uppercase tracking-wide px-2 py-1 rounded border bg-[color:var(--bm-bg-mute)]" data-testid="campaign-status-badge">{campaign.status}</span>
                    {campaign.pause_reason && <span className="text-[11.5px] text-amber-700">Paused: {campaign.pause_reason.replace(/_/g, ' ')}</span>}
                    {campaign.status === 'queued' || campaign.status === 'running' ? (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => doAction('pause')} data-testid="campaign-pause-btn"><PauseCircle size={14} className="mr-1" /> Pause</Button>
                    ) : campaign.status === 'paused' ? (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => doAction('resume')} data-testid="campaign-resume-btn"><PlayCircle size={14} className="mr-1" /> Resume</Button>
                    ) : null}
                    {['queued', 'running', 'paused'].includes(campaign.status) && (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => doAction('cancel', 'Cancel this campaign? Remaining queued emails will not be sent.')}
                                className="text-red-600 border-red-200 hover:bg-red-50" data-testid="campaign-cancel-btn">
                            <XCircle size={14} className="mr-1" /> Cancel
                        </Button>
                    )}
                </div>
            </PageHeader>

            <div className="px-8 py-6 space-y-5">
                {EDITABLE_STATUSES.includes(campaign.status) && editing && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="campaign-edit-panel">
                        <div className="font-display text-[15px] font-semibold mb-4">Edit campaign</div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div className="lg:col-span-2">
                                <CampaignDetailsAndAudience form={editForm} setForm={setEditForm}
                                    companies={[...pinnedCompanies, ...companies.filter(c => !pinnedCompanies.some(p => p.id === c.id))]}
                                    search={search} setSearch={setSearch} companyFilters={companyFilters} setCompanyFilters={setCompanyFilters}
                                    showAudience={campaign.status === 'draft'} />
                            </div>
                            <div>
                                <CampaignCadenceSettings form={editForm} setForm={setEditForm} showAudienceOptions={campaign.status === 'draft'} />
                            </div>
                        </div>
                        <div className="mt-5 pt-5 border-t border-[color:var(--bm-border)] flex items-center justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditing(false)} disabled={saving} className="h-9" data-testid="campaign-edit-cancel-btn">Cancel</Button>
                            <Button onClick={saveEdit} disabled={saving} style={{ background: 'var(--bm-primary)' }} className="h-9" data-testid="campaign-edit-save-btn">
                                {saving ? 'Saving…' : 'Save changes'}
                            </Button>
                        </div>
                    </div>
                )}

                {campaign.status === 'paused' && !editing && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="campaign-paused-edit-panel">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-display text-[15px] font-semibold">Need to fix something before resuming?</div>
                                <p className="text-[12px] text-[color:var(--bm-text-muted)] mt-1">You can still edit the name, subject, body and cadence while paused — the audience is already locked in since recipients were queued.</p>
                            </div>
                            <Button variant="outline" size="sm" onClick={startEdit} className="h-8 shrink-0" data-testid="campaign-edit-btn"><Pencil size={13} className="mr-1.5" /> Edit</Button>
                        </div>
                    </div>
                )}

                {campaign.status === 'draft' && !editing && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6" data-testid="campaign-draft-panel">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-display text-[15px] font-semibold">Before you queue this campaign</div>
                            <Button variant="outline" size="sm" onClick={startEdit} className="h-8" data-testid="campaign-edit-btn"><Pencil size={13} className="mr-1.5" /> Edit</Button>
                        </div>
                        <Button variant="outline" onClick={runPreview} data-testid="campaign-run-preview-btn"><Eye size={14} className="mr-1.5" /> Preview selection & sample emails</Button>

                        {preview && (
                            <div className="mt-4">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                                    <Stat label="Companies" value={preview.companies_selected} />
                                    <Stat label="Contacts matching" value={preview.contacts_matching} />
                                    <Stat label="Already contacted" value={preview.already_contacted} />
                                    <Stat label="Do Not Contact" value={preview.do_not_contact} accent="red" />
                                    <Stat label="Verified emails" value={preview.verified_emails} accent="emerald" />
                                    <Stat label="Inferred emails" value={preview.inferred_emails} accent="amber" />
                                    <Stat label="Total to send" value={preview.total_to_send} accent="emerald" bold />
                                </div>
                                {preview.previews.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        <div className="text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Sample personalised emails</div>
                                        {preview.previews.map((p, i) => (
                                            <div key={i} className="border border-[color:var(--bm-border)] rounded-lg p-3 text-[12.5px]" data-testid={`campaign-preview-sample-${i}`}>
                                                <div className="font-medium">{p.contact_name} · {p.company_name} · <span className="text-[color:var(--bm-text-muted)]">{p.email}</span></div>
                                                <div className="mt-1 font-semibold">{p.subject}</div>
                                                <div className="mt-1 text-[color:var(--bm-text-muted)]" dangerouslySetInnerHTML={{ __html: p.body_html }} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-5 pt-5 border-t border-[color:var(--bm-border)] flex items-center gap-2 flex-wrap">
                            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Send a test to…" className="h-9 max-w-[260px]" data-testid="campaign-test-email-input" />
                            <Button variant="outline" onClick={testSend} disabled={busy} className="h-9" data-testid="campaign-test-send-btn"><Send size={14} className="mr-1.5" /> Send test</Button>
                            <div className="flex-1" />
                            <Button onClick={() => doAction('queue')} disabled={busy} style={{ background: 'var(--bm-primary)' }} className="h-9" data-testid="campaign-queue-btn">
                                Queue campaign
                            </Button>
                        </div>
                    </div>
                )}

                {dashboard && (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="campaign-dashboard-tiles">
                            {DASHBOARD_TILES.map(k => (
                                <button key={k} onClick={() => loadRecipients(k)}
                                        className={`rounded-lg border p-3 text-left transition ${activeFilter === k ? 'border-[color:var(--bm-primary)] bg-[color:var(--bm-accent-50)]' : 'border-[color:var(--bm-border)] bg-white hover:bg-[color:var(--bm-bg-mute)]'}`}
                                        data-testid={`campaign-tile-${k}`}>
                                    <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{k === 'remaining' ? 'Remaining' : CONTACT_STATUS_LABELS[k] || k}</div>
                                    <div className="mt-1 font-display text-[18px] font-semibold">{dashboard[k] ?? 0}</div>
                                </button>
                            ))}
                        </div>

                        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--bm-border)]">
                                <div className="font-display text-[15px] font-semibold">
                                    {activeFilter ? `${activeFilter === 'remaining' ? 'Remaining' : CONTACT_STATUS_LABELS[activeFilter] || activeFilter} contacts` : `All ${dashboard.total_contacts} contacts`}
                                </div>
                                {activeFilter && <button onClick={() => loadRecipients(null)} className="text-[12px] text-[color:var(--bm-text-muted)] underline">Clear filter</button>}
                            </div>
                            {(activeFilter ? recipients : []).length === 0 && !activeFilter && (
                                <div className="px-5 py-8 text-center text-[13px] text-[color:var(--bm-text-muted)]">Click a tile above to see the underlying contacts.</div>
                            )}
                            {recipients.length > 0 && (
                                <table className="w-full text-[13px]">
                                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                                        <tr>
                                            <th className="text-left font-medium px-5 py-2.5">Contact</th>
                                            <th className="text-left font-medium px-4 py-2.5">Company</th>
                                            <th className="text-left font-medium px-4 py-2.5">Email</th>
                                            <th className="text-left font-medium px-4 py-2.5">Sent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recipients.map(r => (
                                            <tr key={r.recipient_id} onClick={() => navigate(`/clients/${r.client_id}`)} className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer" data-testid={`campaign-recipient-row-${r.recipient_id}`}>
                                                <td className="px-5 py-2.5 font-medium">{r.contact_name}</td>
                                                <td className="px-4 py-2.5 text-[color:var(--bm-text-muted)]">{r.company_name}</td>
                                                <td className="px-4 py-2.5 text-[color:var(--bm-text-muted)]">{r.email}</td>
                                                <td className="px-4 py-2.5 text-[color:var(--bm-text-muted)]">{r.sent_at ? new Date(r.sent_at).toLocaleString('en-GB') : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, accent, bold }) {
    const toneMap = { emerald: 'text-emerald-700', amber: 'text-amber-700', red: 'text-red-700' };
    return (
        <div className="rounded-lg border border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`mt-1 font-display ${bold ? 'text-[20px]' : 'text-[16px]'} font-semibold ${toneMap[accent] || ''}`}>{value}</div>
        </div>
    );
}
